import { ChangeEvent, useCallback, useEffect, useState } from 'react'

import CardSurface from '../../components/card-surface/CardSurface'
import Button from '../../components/button/Button'
import {
  getMaintenance,
  getMaintenanceCategories,
  submitMaintenance,
  uploadMaintenanceImage,
} from '../../_data/resident'
import {
  MaintenanceCategory,
  MaintenanceRequestPriority,
  ResidentMaintenanceRequest,
} from '../../_types/resident'
import {
  formatLedgerDate,
  formatMaintenancePriority,
  formatMaintenanceStatus,
} from '../../_utilities/resident-format'
import { compressImageToDataUrl } from '../../_utilities/compress-image'
import { MAX_MAINTENANCE_IMAGES, PRIORITY_OPTIONS } from './helpers'
import { MaintenanceSectionProps } from './types'
import {
  QuietText,
  StatusPill,
  LedgerDescription,
  LedgerMeta,
  BalanceActions,
  MaintenanceList,
  MaintenanceRow,
  MaintenanceRowMain,
  MaintenanceForm,
  FormField,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  PhotoPicker,
  PhotoRow,
  PhotoThumbWrapper,
  PhotoThumbImage,
  RemovePhotoButton,
  AddPhotoLabel,
  HiddenFileInput,
  PhotoError,
} from './styled'

/**
 * The renter's maintenance card for a single tenancy: lists their requests with
 * status, and a "Report an issue" form (title, description, category, priority).
 * Categories are loaded live from the company's configured taxonomy (active
 * only); when a category carries troubleshooting steps they are surfaced as a
 * gentle self-service hint before submitting. The category picker is omitted
 * entirely when the company has none configured. Renters can attach up to
 * MAX_MAINTENANCE_IMAGES photos, compressed client-side and uploaded before
 * submit.
 *
 * @param {MaintenanceSectionProps} props
 * @returns {JSX.Element}
 */
const MaintenanceSection = ({
  tenancyId,
}: MaintenanceSectionProps): JSX.Element => {
  const [requests, setRequests] = useState<ResidentMaintenanceRequest[]>([])
  const [categories, setCategories] = useState<MaintenanceCategory[]>([])
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [priority, setPriority] = useState<MaintenanceRequestPriority>('normal')
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [uploadingImageCount, setUploadingImageCount] = useState<number>(0)
  const [imageError, setImageError] = useState<string>('')

  const loadRequests = useCallback(async () => {
    const loaded = await getMaintenance(tenancyId)
    setRequests(loaded)
  }, [tenancyId])

  const loadCategories = useCallback(async () => {
    const loaded = await getMaintenanceCategories(tenancyId)
    setCategories(loaded)
  }, [tenancyId])

  useEffect(() => {
    void loadRequests()
    void loadCategories()
  }, [loadRequests, loadCategories])

  const resetForm = useCallback(() => {
    setTitle('')
    setDescription('')
    setCategoryId('')
    setPriority('normal')
    setImageUrls([])
    setUploadingImageCount(0)
    setImageError('')
  }, [])

  const handleSelectImages = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const fileList = changeEvent.target.files
      changeEvent.target.value = ''
      if (fileList === null) {
        return
      }

      const remainingSlots =
        MAX_MAINTENANCE_IMAGES - imageUrls.length - uploadingImageCount
      const filesToUpload = Array.from(fileList).slice(
        0,
        Math.max(remainingSlots, 0),
      )
      if (filesToUpload.length === 0) {
        return
      }

      setImageError('')
      filesToUpload.forEach(file => {
        setUploadingImageCount(previous => previous + 1)
        void compressImageToDataUrl(file)
          .then(dataUrl => uploadMaintenanceImage(tenancyId, dataUrl))
          .then(uploadedUrl => {
            setUploadingImageCount(previous => previous - 1)
            if (uploadedUrl === null) {
              setImageError('We couldn’t upload a photo — please try again.')
              return
            }
            setImageUrls(previous =>
              previous.length >= MAX_MAINTENANCE_IMAGES
                ? previous
                : [...previous, uploadedUrl],
            )
          })
          .catch(() => {
            setUploadingImageCount(previous => previous - 1)
            setImageError('That file couldn’t be added — please choose an image.')
          })
      })
    },
    [tenancyId, imageUrls.length, uploadingImageCount],
  )

  const handleRemoveImage = useCallback((url: string) => {
    setImageUrls(previous => previous.filter(existing => existing !== url))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (title.trim() === '' || description.trim() === '') {
      return
    }

    setIsSubmitting(true)
    const created = await submitMaintenance(tenancyId, {
      title: title.trim(),
      description: description.trim(),
      categoryId: categoryId === '' ? null : categoryId,
      priority,
      imageUrls,
    })
    setIsSubmitting(false)

    if (created === null) {
      return
    }

    resetForm()
    setIsFormOpen(false)
    void loadRequests()
  }, [
    tenancyId,
    title,
    description,
    categoryId,
    priority,
    imageUrls,
    resetForm,
    loadRequests,
  ])

  const isSubmitDisabled =
    isSubmitting ||
    uploadingImageCount > 0 ||
    title.trim() === '' ||
    description.trim() === ''

  const selectedCategory =
    categories.find(category => category.id === categoryId) ?? null

  return (
    <CardSurface padding="standard">
      {requests.length === 0 && isFormOpen === false ? (
        <QuietText>
          No maintenance requests yet. Report an issue and your property manager
          will see it right away.
        </QuietText>
      ) : null}

      {requests.length > 0 ? (
        <MaintenanceList>
          {requests.map(request => (
            <MaintenanceRow key={request.id}>
              <MaintenanceRowMain>
                <LedgerDescription>{request.title}</LedgerDescription>
                <LedgerMeta>
                  {formatLedgerDate(request.createdAt)} ·{' '}
                  {formatMaintenancePriority(request.priority)}
                </LedgerMeta>
              </MaintenanceRowMain>
              <StatusPill>{formatMaintenanceStatus(request.status)}</StatusPill>
            </MaintenanceRow>
          ))}
        </MaintenanceList>
      ) : null}

      {isFormOpen ? (
        <MaintenanceForm>
          <FormField>
            <FormLabel htmlFor="maintenance-title">Title</FormLabel>
            <FormInput
              id="maintenance-title"
              value={title}
              placeholder="e.g. Leaky kitchen faucet"
              onChange={changeEvent => setTitle(changeEvent.target.value)}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="maintenance-description">Description</FormLabel>
            <FormTextarea
              id="maintenance-description"
              value={description}
              placeholder="Describe the issue so your PM can help fast."
              onChange={changeEvent => setDescription(changeEvent.target.value)}
            />
          </FormField>
          {categories.length > 0 ? (
            <FormField>
              <FormLabel htmlFor="maintenance-category">Category</FormLabel>
              <FormSelect
                id="maintenance-category"
                value={categoryId}
                onChange={changeEvent =>
                  setCategoryId(changeEvent.target.value)
                }
              >
                <option value="">Choose a category (optional)</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </FormSelect>
              {selectedCategory !== null &&
              selectedCategory.troubleshootingSteps !== '' ? (
                <QuietText>
                  Before we send someone:{' '}
                  {selectedCategory.troubleshootingSteps}
                </QuietText>
              ) : null}
            </FormField>
          ) : null}
          <FormField>
            <FormLabel htmlFor="maintenance-priority">Priority</FormLabel>
            <FormSelect
              id="maintenance-priority"
              value={priority}
              onChange={changeEvent =>
                setPriority(
                  changeEvent.target.value as MaintenanceRequestPriority,
                )
              }
            >
              {PRIORITY_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {formatMaintenancePriority(option)}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <FormField>
            <FormLabel>Photos (optional)</FormLabel>
            <PhotoPicker>
              {imageUrls.length > 0 ? (
                <PhotoRow>
                  {imageUrls.map(url => (
                    <PhotoThumbWrapper key={url}>
                      <PhotoThumbImage src={url} alt="Attached photo" />
                      <RemovePhotoButton
                        type="button"
                        aria-label="Remove photo"
                        onClick={() => handleRemoveImage(url)}>
                        ×
                      </RemovePhotoButton>
                    </PhotoThumbWrapper>
                  ))}
                </PhotoRow>
              ) : null}
              {imageUrls.length + uploadingImageCount <
              MAX_MAINTENANCE_IMAGES ? (
                <AddPhotoLabel>
                  {uploadingImageCount > 0 ? 'Uploading…' : 'Add photos'}
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleSelectImages}
                  />
                </AddPhotoLabel>
              ) : null}
              {imageError !== '' ? <PhotoError>{imageError}</PhotoError> : null}
            </PhotoPicker>
          </FormField>
          <BalanceActions>
            <Button
              type="button"
              variant="primary"
              size="small"
              isLoading={isSubmitting}
              disabled={isSubmitDisabled}
              onClick={() => {
                void handleSubmit()
              }}
            >
              Submit request
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="small"
              disabled={isSubmitting}
              onClick={() => {
                setIsFormOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
          </BalanceActions>
        </MaintenanceForm>
      ) : (
        <BalanceActions>
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={() => setIsFormOpen(true)}
          >
            Report an issue
          </Button>
        </BalanceActions>
      )}
    </CardSurface>
  )
}

export default MaintenanceSection
